import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, Separator, Loader, BizcoverIndiaBrandMark } from "@b2b/ui-components";
import { Mail, Lock, LogIn, X, XCircle, Eye, EyeOff } from "lucide-react";
import {
  authService,
  setAccessToken,
  setRefreshToken,
  membershipApi,
} from "@b2b/services";
import { usePostLoginRedirect, buildPathFromStoredLocation } from "./hooks/usePostLoginRedirect";
import FormInput from "@b2b/ui-components/features/Registration/components/FormInput";
import { useAuth } from "./Authprovider";
import { invalidateHeaderUserCache } from "./headerUserCache";
import {
  MultiStepRegistration,
  type SignupAuthLayout,
} from "@b2b/ui-components";
type PricePlanOption = { id: string; name: string };

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: string;
              size?: string;
              type?: string;
              text?: string;
            },
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface Toast {
  type: "error" | "success";
  message: string;
}

type AuthTab = "signin" | "signup";

/** Resolves message after axios interceptor wraps failures in ApiError (message on `error.message`). */
function getServerErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
    name?: string;
  };
  const raw = e.response?.data?.message;
  if (raw != null) {
    return Array.isArray(raw) ? raw.join(" ") : String(raw);
  }
  if (e.name === "ApiError" && typeof e.message === "string" && e.message) {
    return e.message;
  }
  return undefined;
}

function getErrorHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as { status?: number; response?: { status?: number } };
  return e.status ?? e.response?.status;
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";
const REDIRECT_DELAY_MS = 450;

const LoginPage: React.FC = () => {
  const {
    isAuthenticated,
    isLoading: authLoading,
    isSessionProfileLoading,
    sessionEmail,
    userStatus,
    userRole,
    refetchUserStatus,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handlePostLogin } = usePostLoginRedirect();
  const locationState = location.state as {
    fromOAuth?: boolean;
    prefilledEmail?: string;
    from?: { pathname?: string; search?: string; hash?: string };
  } | null;
  const [googleButtonContainer, setGoogleButtonContainer] =
    useState<HTMLDivElement | null>(null);
  const [redirectingToProfile, setRedirectingToProfile] = useState<string | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tabFromUrl = searchParams.get("tab") === "signup" ? "signup" : "signin";
  const [activeTab, setActiveTab] = useState<AuthTab>(tabFromUrl);

  const signupStartsAtWideStep = Boolean(
    locationState?.fromOAuth ||
      (isAuthenticated &&
        (userStatus === "pending_registration" ||
          userStatus === "not_registered")),
  );
  const [signupAuthLayout, setSignupAuthLayout] = useState<SignupAuthLayout>(
    () => (tabFromUrl === "signup" && signupStartsAtWideStep ? "wide" : "compact"),
  );

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    setToast(null);
    setErrors({});
    setEmail("");
    setPassword("");
    setShowSignInPassword(false);
  }, [activeTab]);

  const handleSignupAuthLayoutChange = useCallback((layout: SignupAuthLayout) => {
    setSignupAuthLayout((prev) => (prev === layout ? prev : layout));
  }, []);

  const setTab = (t: AuthTab) => {
    setActiveTab(t);
    if (t === "signup") {
      setSearchParams({ tab: "signup" }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {},
  );
  const [toast, setToast] = useState<Toast | null>(null);
  const [pricePlans, setPricePlans] = useState<PricePlanOption[]>([]);

  useEffect(() => {
    let active = true;
    membershipApi
      .getActivePlans()
      .then((plans) => {
        if (!active) return;
        setPricePlans(
          plans.map((p) => ({ id: p.id, name: p.name })),
        );
      })
      .catch(() => {
        if (!active) return;
        setPricePlans([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // When already authenticated and fully registered (active), redirect to profile by role.
  // Do NOT redirect when userStatus is pending_registration or not_registered (OAuth signup must complete form first).
  useEffect(() => {
    if (authLoading || isSessionProfileLoading || redirectingToProfile) return;
    if (!isAuthenticated || userStatus !== "active") return;

    if (locationState?.from?.pathname) {
      navigate(buildPathFromStoredLocation(locationState.from), { replace: true });
      return;
    }

    if (userRole === "buyer") {
      navigate("/buyer/profile_settings", { replace: true });
    } else if (userRole === "seller") {
      navigate("/seller/profile_settings", { replace: true });
    } else if (userRole === "admin") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [
    isAuthenticated,
    authLoading,
    isSessionProfileLoading,
    redirectingToProfile,
    userStatus,
    userRole,
    navigate,
    locationState?.from,
  ]);

  // OAuth users with pending_registration or not_registered must complete signup form: show signup tab
  useEffect(() => {
    if (!isAuthenticated || authLoading || isSessionProfileLoading) return;
    if (userStatus === "pending_registration" || userStatus === "not_registered") {
      setActiveTab("signup");
      setSearchParams({ tab: "signup" }, { replace: true });
    }
  }, [isAuthenticated, authLoading, isSessionProfileLoading, userStatus]);

  const handleGoogleCredential = async (credential: string) => {
    setGoogleLoading(true);
    setToast(null);
    try {
      const response = await authService.loginWithGoogle(credential, "sign-in");
      const accessToken = response?.accessToken;
      if (accessToken) {
        setAccessToken(accessToken);
        if (response.refreshToken) setRefreshToken(response.refreshToken);
        await handlePostLogin(accessToken);
      } else {
        throw new Error("No access token received");
      }
    } catch (error: unknown) {
      console.error("Google login error:", error);
      setToast({
        type: "error",
        message:
          getServerErrorMessage(error) ||
          "Google sign-in failed. Please try again.",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCredentialRef = useRef(handleGoogleCredential);
  handleGoogleCredentialRef.current = handleGoogleCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonContainer) return;

    if (import.meta.env.DEV) {
      const origin = window.location.origin;
      const clientIdPreview = `${GOOGLE_CLIENT_ID.slice(0, 12)}...${GOOGLE_CLIENT_ID.slice(-8)}`;
      console.info(
        "[Google Sign-In] Origin (must be in Authorized JavaScript origins):",
        origin,
        "| Client ID:",
        clientIdPreview,
      );
    }

    const initAndRender = () => {
      if (!window.google?.accounts?.id || !googleButtonContainer) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential: string }) => {
          handleGoogleCredentialRef.current(response.credential);
        },
      });
      try {
        window.google.accounts.id.renderButton(googleButtonContainer, {
          theme: "outline",
          size: "large",
          type: "standard",
          text: "continue_with",
        });
      } catch {
        window.google.accounts.id.renderButton(googleButtonContainer, {});
      }
    };

    if (window.google?.accounts?.id) {
      initAndRender();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => initAndRender();
    document.head.appendChild(script);
  }, [googleButtonContainer]);

  const validateEmail = (emailVal: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
  };

  const validateSignInForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email))
      newErrors.email = "Please enter a valid email address";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setToast(null);
    if (!validateSignInForm()) return;

    setIsLoading(true);
    try {
      const response = await authService.login({
        email: email.trim(),
        password,
      });
      const accessToken = response.accessToken;
      if (accessToken) {
        setAccessToken(accessToken);
        if (response.refreshToken) setRefreshToken(response.refreshToken);
        await handlePostLogin(accessToken);
      } else {
        throw new Error("No access token received");
      }
    } catch (error: unknown) {
      const status = getErrorHttpStatus(error);
      const serverMsg = getServerErrorMessage(error);
      if (status === 401) {
        setToast({
          type: "error",
          message: serverMsg || "Invalid credentials.",
        });
      } else if (serverMsg) {
        setToast({ type: "error", message: serverMsg });
      } else {
        setToast({
          type: "error",
          message: "An error occurred. Please try again.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (data: {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    phone?: string;
    country?: string;
    role?: string;
    captchaToken?: string;
    pricePlanId?: string;
  }) => {
    if (!data.email) throw new Error("Email is required");
    const res = await authService.register({
      email: data.email,
      password: (data.password ?? "").trim(),
      firstName: (data.firstName ?? "").trim(),
      lastName: (data.lastName ?? "").trim(),
      companyName: (data.companyName ?? "").trim(),
      phone: data.phone?.trim(),
      country: data.country?.trim(),
      role: (data.role === "seller"
        ? "seller"
        : data.role === "both"
          ? "both"
          : "buyer") as "buyer" | "seller" | "both",
      captchaToken: data.captchaToken?.trim() || undefined,
      pricePlanId: data.pricePlanId?.trim() || undefined,
    });
    if (res?.accessToken) setAccessToken(res.accessToken);
    if (res?.refreshToken) setRefreshToken(res.refreshToken);
    return res;
  };

  const handleRegistrationSuccess = (userRole: string) => {
    invalidateHeaderUserCache();
    refetchUserStatus(); // So AuthProvider gets status "active" after OAuth sync
    const targetPath =
      userRole === "buyer"
        ? "/buyer/profile_settings"
        : userRole === "seller" || userRole === "both"
          ? "/seller/profile_settings"
          : userRole === "admin"
            ? "/admin/dashboard"
            : "/buyer/profile_settings";
    setRedirectingToProfile(targetPath);
  };

  // Delayed redirect after signup so AuthProvider can pick up token
  useEffect(() => {
    if (!redirectingToProfile) return;
    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;
      navigate(redirectingToProfile, { replace: true });
      setRedirectingToProfile(null);
    }, REDIRECT_DELAY_MS);
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [redirectingToProfile, navigate]);

  const handleGetUserStatus = async () => {
    const response = await authService.getMe();
    return response;
  };

  const handleSyncUser = async (syncData: unknown) => {
    const response = await authService.syncUser(
      syncData as Parameters<typeof authService.syncUser>[0],
    );
    return response;
  };

  const handleGoogleSignUpCredential = async (credential: string) => {
    const response = await authService.loginWithGoogle(credential, "sign-up");
    const accessToken = response?.accessToken;
    if (accessToken) setAccessToken(accessToken);
    if (response.refreshToken) setRefreshToken(response.refreshToken);
    const email =
      (response as { user?: { email?: string } })?.user?.email ?? "";
    return email ? { email } : null;
  };

  if (redirectingToProfile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <Loader size={48} color="#f97316" />
        <p className="mt-4 text-gray-600 font-medium">Taking you to your profile...</p>
        <p className="mt-1 text-sm text-gray-500">Please wait</p>
      </div>
    );
  }

  // Show full-page loader only when we have no UI to render inline. The signup tab keeps its own
  // form mounted while /me is in flight so the multi-step Google flow doesn't lose state.
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <Loader size={40} color="#f97316" />
        <p className="ml-4 text-gray-600">Checking authentication...</p>
      </div>
    );
  }

  if (
    isAuthenticated &&
    isSessionProfileLoading &&
    activeTab !== "signup"
  ) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <Loader size={40} color="#f97316" />
        <p className="ml-4 text-gray-600">Loading your account...</p>
      </div>
    );
  }

  // Only show "Redirecting..." when user is fully registered (active). Pending OAuth signups see the form below.
  if (isAuthenticated && userStatus === "active") {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <Loader size={40} color="#f97316" />
        <p className="ml-4 text-gray-600">Redirecting...</p>
      </div>
    );
  }

  if (isLoading || googleLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100">
        <Loader size={48} color="#f97316" />
        <p className="mt-4 text-gray-600 font-medium">
          {googleLoading ? "Signing in with Google..." : "Signing in..."}
        </p>
        <p className="mt-1 text-sm text-gray-500">Please wait</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#f9fafb]">
      <header className="absolute left-6 top-6 z-10 flex flex-col gap-1 md:left-8 md:top-8">
        <BizcoverIndiaBrandMark variant="auth" to="/bizcoverindia-home-updated" />
        <p className="hidden text-xs text-gray-600 sm:block">Your trusted B2B marketplace</p>
      </header>

      <div className="flex min-h-screen flex-col items-center justify-center overflow-y-auto px-4 py-10 sm:px-6 md:px-8 md:py-12">
        <div
          className={`mx-auto flex w-full min-w-0 flex-col ${
            activeTab === "signup" && signupAuthLayout === "wide"
              ? "max-w-2xl"
              : "max-w-md"
          }`}
        >
            <div
              className="w-full min-w-0 shrink-0 rounded-2xl border border-[#EEF0F3] bg-white p-4 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)] transition-shadow sm:p-6 md:p-10"
            >
              <div className="mb-6 flex rounded-xl bg-[#f3f4f6] p-1">
                <button
                  type="button"
                  onClick={() => setTab("signin")}
                  className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition-all ${
                    activeTab === "signin"
                      ? "bg-white text-[#f15a11] shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  aria-selected={activeTab === "signin"}
                  role="tab"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => setTab("signup")}
                  className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition-all ${
                    activeTab === "signup"
                      ? "bg-white text-[#f15a11] shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  aria-selected={activeTab === "signup"}
                  role="tab"
                >
                  Sign Up
                </button>
              </div>

              {toast && (
                <div
                  className={`flex items-center gap-2 rounded-lg p-4 mb-6 ${
                    toast.type === "error"
                      ? "bg-red-50 border border-red-200 text-red-800"
                      : "bg-green-50 border border-green-200 text-green-800"
                  }`}
                >
                  <XCircle
                    className={`w-5 h-5 flex-shrink-0 ${toast.type === "error" ? "text-red-600" : "text-green-600"}`}
                  />
                  <p className="flex-1 text-sm font-medium">{toast.message}</p>
                  <button
                    onClick={() => setToast(null)}
                    className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
                    aria-label="Close notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="relative w-full min-w-0">
              <div
                className={`space-y-6 ${activeTab === "signin" ? "" : "hidden"}`}
                aria-hidden={activeTab !== "signin"}
              >
                  {GOOGLE_CLIENT_ID && (
                    <>
                      <div
                        ref={setGoogleButtonContainer}
                        className="flex justify-center [&>div]:!w-full [&>div]:!flex [&>div]:!justify-center"
                      />
                      <div className="relative">
                        <Separator className="bg-[#EDEEF2]" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="bg-white px-4 text-sm font-medium text-gray-500">
                            OR
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <form onSubmit={handleEmailLogin} className="space-y-5">
                    <div className="space-y-2">
                      <FormInput
                        id="email"
                        label="Email"
                        icon={Mail}
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        required
                        disabled={isLoading}
                        autoFocus
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        visualVariant="authSignin"
                      />
                      {errors.email && (
                        <p id="email-error" className="text-sm text-red-600 mt-1" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <FormInput
                        id="password"
                        label="Password"
                        icon={Lock}
                        type={showSignInPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errors.password) setErrors({ ...errors, password: undefined });
                        }}
                        required
                        disabled={isLoading}
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? "password-error" : undefined}
                        visualVariant="authSignin"
                        endAdornment={
                          <button
                            type="button"
                            className="rounded p-1 text-gray-400 hover:text-[#f15a11] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50"
                            onClick={() => setShowSignInPassword((p) => !p)}
                            disabled={isLoading}
                            aria-label={
                              showSignInPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showSignInPassword ? (
                              <EyeOff className="h-4 w-4 shrink-0" aria-hidden />
                            ) : (
                              <Eye className="h-4 w-4 shrink-0" aria-hidden />
                            )}
                          </button>
                        }
                      />
                      {errors.password && (
                        <p id="password-error" className="text-sm text-red-600 mt-1" role="alert">
                          {errors.password}
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end -mt-1">
                      <button
                        type="button"
                        className="text-sm font-bold text-[#f15a11] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 focus-visible:ring-offset-2 rounded-sm bg-transparent border-0 p-0 cursor-pointer"
                        onClick={() =>
                          navigate("/forgot-password", {
                            state: { email: email.trim() },
                          })
                        }
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <Button
                      type="submit"
                      variant="ctaPrimary"
                      size="lg"
                      className="h-12 w-full rounded-xl bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Signing in...
                        </div>
                      ) : (
                        <>
                          <LogIn className="w-5 h-5" />
                          Sign In
                        </>
                      )}
                    </Button>
                  </form>
              </div>

              <div
                className={`w-full min-w-0 ${activeTab === "signup" ? "" : "hidden"}`}
                aria-hidden={activeTab !== "signup"}
              >
                  <MultiStepRegistration
                    googleClientId={GOOGLE_CLIENT_ID || undefined}
                    prefilledEmailFromOAuth={
                      locationState?.fromOAuth
                        ? locationState?.prefilledEmail
                        : (isAuthenticated &&
                            (userStatus === "pending_registration" ||
                              userStatus === "not_registered"))
                          ? sessionEmail
                          : undefined
                    }
                    onGoogleCredential={handleGoogleSignUpCredential}
                    onSendSignupOtp={(email) => authService.sendSignupOtp(email)}
                    onVerifySignupOtp={(email, otp) =>
                      authService.verifySignupOtp(email, otp)
                    }
                    onRegister={handleRegister}
                    onRegistrationSuccess={handleRegistrationSuccess}
                    onSyncUser={handleSyncUser}
                    onGetUserStatus={handleGetUserStatus}
                    onCheckShopNameAvailable={(name) =>
                      authService.checkShopNameAvailable(name)
                    }
                    recaptchaSiteKey={
                      import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? ""
                    }
                    pricePlans={pricePlans}
                    onSwitchToSignIn={() => setTab("signin")}
                    onAuthLayoutChange={handleSignupAuthLayoutChange}
                  />
              </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
